import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import Colors from '../../utils/color';
import { HITL7_CHIP_FONT_SIZE } from '../../utils/hitl3Typography';
import { HITL7_CHIP_GAP } from '../../utils/hitl3Spacing';
import { Band, bandForPct } from './PulseMeterTile';

/**
 * PULSE DIAL CHIP (HITL 7.0) — the Response-time meter as a single chip.
 *
 * HITL-1663 replaces the Copilot card with a chip strip, so the one metric that
 * survives has to live in the same visual language as the signal pills beside
 * it: a rounded pill, one line tall, sized to its content. This is that pill —
 * a small ring dial that fills as the reply clock runs, the whole-second numeral
 * inside it, and the label alongside.
 *
 * It is a 7.0-only surface. With the flag off the Response tile keeps rendering
 * PulseMeterTile (value + label + bar), so nothing in the 3.0 strip moves.
 *
 * Thresholds are imported from PulseMeterTile rather than re-stated, so the two
 * shapes of the same metric can never disagree about when amber starts.
 *
 * Colours are the 7.0 set (header green / intent-button amber / Staff-button
 * red), matching the status band and the header countdown that landed earlier
 * in this release, rather than PulseMeterTile's emerald/bittersweet. Two
 * different greens a few pixels apart is the thing that reads as a bug.
 *
 * Purely presentational: the caller decides visibility and supplies the elapsed
 * time and the limit.
 */

// Ring geometry. A 20px box, r=8.5, so a 2px stroke sits fully inside it and
// leaves ~15px clear in the middle — enough for two digits at the numeral size
// below. The ring is deliberately no taller than the 12px chip text plus its
// padding: it sets the height of the whole strip row, so a bigger dial would
// undo the point of shrinking the chips around it.
const BOX = 20;
const CENTRE = BOX / 2;
const RADIUS = 8.5;
const STROKE = 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// The 7.0 status colours. brightGreen is the header's mic/wifi green and the
// status band's calm green — deliberately the same token, even though a 2.5px
// arc of it on white is a light mark. The numeral, not the ring, is what has to
// be readable; the ring is the at-a-glance state, and the over-limit case also
// turns the chip's border red so escalation never rests on the arc alone.
const dialColor = (band: Band): string => {
  switch (band) {
    case 'green':
      return Colors.brightGreen; // #70e000
    case 'yellow':
      return Colors.acknowledge; // #FFC107 — the intent buttons' amber
    default:
      return Colors.requestStaff; // #FF0048 — the Staff button's red
  }
};

const useStyles = makeStyles((theme: Theme) => ({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: HITL7_CHIP_GAP,
    padding: theme.spacing(0.25, 1, 0.25, 0.375),
    borderRadius: 999,
    backgroundColor: Colors.white,
    border: `1px solid ${Colors.alto}`,
    fontSize: HITL7_CHIP_FONT_SIZE,
    fontWeight: 500,
    color: Colors.mineShaft,
    // The strip wraps; a chip must never be the thing that forces a h-scroll.
    flexShrink: 0,
  },
  // Over the limit the whole pill outlines red, so the breach is legible
  // without relying on the thin arc.
  chipOver: {
    borderColor: Colors.requestStaff,
  },
  dial: {
    display: 'block',
    flexShrink: 0,
  },
  // Centred inside the ring. Tabular so 9 → 10 doesn't shift the digits.
  numeral: {
    fontSize: '0.58rem',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    fill: Colors.primaryButtonTextColor,
  },
  label: {
    whiteSpace: 'nowrap',
  },
}));

export interface PulseDialChipProps {
  label: string;
  /** Elapsed time in ms. */
  elapsedMs: number;
  /** Threshold in ms; <= 0 keeps the dial empty and green. */
  limitMs: number;
  /** Formats the numeral shown inside the ring (e.g. "4"). */
  formatValue: (ms: number) => string;
  testId?: string;
}

const PulseDialChip = ({
  label,
  elapsedMs,
  limitMs,
  formatValue,
  testId,
}: PulseDialChipProps) => {
  const classes = useStyles();

  const pct = limitMs > 0 ? (elapsedMs / limitMs) * 100 : 0;
  const band = bandForPct(pct);
  const color = dialColor(band);
  // Clamp: past the limit the ring stays full rather than winding round again.
  const filled = Math.max(0, Math.min(100, pct));
  const dash = (filled / 100) * CIRCUMFERENCE;

  return (
    <span
      className={`${classes.chip}${
        band === 'red' ? ` ${classes.chipOver}` : ''
      }`}
      data-testid={testId}
      data-band={band}
    >
      <svg
        className={classes.dial}
        width={BOX}
        height={BOX}
        viewBox={`0 0 ${BOX} ${BOX}`}
        aria-hidden="true"
        focusable="false"
      >
        <circle
          cx={CENTRE}
          cy={CENTRE}
          r={RADIUS}
          fill="none"
          stroke={Colors.gallery}
          strokeWidth={STROKE}
        />
        {/* Rotated so the arc starts at 12 o'clock and runs clockwise. */}
        <circle
          data-testid="pulse-dial-arc"
          cx={CENTRE}
          cy={CENTRE}
          r={RADIUS}
          fill="none"
          stroke={color}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${CIRCUMFERENCE}`}
          transform={`rotate(-90 ${CENTRE} ${CENTRE})`}
        />
        <text
          className={classes.numeral}
          x={CENTRE}
          y={CENTRE}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {formatValue(elapsedMs)}
        </text>
      </svg>
      <span className={classes.label}>{label}</span>
    </span>
  );
};

export default PulseDialChip;
